<template>
  <PForm
    ref="pForm"
    :heading="formHeading"
    :primary-cta="submitButton"
    :secondary-cta="secondaryCta"
    :show-footer="formState.preview"
    @close="closeForm"
    @submit.prevent="onSubmit"
    @reset="resetForm"
  >
    <div v-if="!formState.preview" class="border-none q-ma-none q-pa-none">
      <div class="q-mb-md">
        <span class="q-mr-xxs">Status:</span>
        <span
          :class="
            formData.status_cd === 'active' ? 'text-info-50' : 'text-warning-70'
          "
        >
          {{ useChangeCase(formData.status_cd, "capitalCase")?.value || "-" }}
        </span>
      </div>
    </div>

    <q-separator v-if="!formState.preview" class="q-mb-xl q-mt-sm" />

    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <PInput
            v-model.trim="debitNoteNumber"
            :disable="Boolean(formState.view || formState.preview)"
            :loading="validatingNumber"
            :placeholder="
              Boolean(formState.preview) ? 'Not generated' : 'Enter value'
            "
            :rules="[validateNumber]"
            debounce="300"
            label="Debit Note No."
            required
          />
        </div>
        <div class="col-6">
          <PVDateInput
            v-model="formData.date"
            label="Debit Note Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="Boolean(formState.view || formState.preview)"
            :min-date="currentDate"
            :max-date="currentDate"
          />
        </div>

        <div v-if="formState.preview" class="col-6">
          <PInput v-model.trim="formData.status" label="DN Status" disable />
        </div>
        <div v-if="formState.preview" class="col-6">
          <PInput v-model.trim="formData.stage" label="DN Stage" disable />
        </div>

        <div v-if="!formState.preview" class="col-6">
          <div class="row">
            <template
              v-for="({ label, value }, index) in debitNoteInfo"
              :key="index"
            >
              <div v-if="label" class="col-4">
                <span class="text-neutral-80">{{ label }}</span>
              </div>
              <div v-if="label" class="col-8">
                <PBadge
                  v-if="label === 'DN Against'"
                  :label="value || '-'"
                  :type="value"
                  class="text-capitalize"
                />
                <span v-else class="text-neutral text-wrap">
                  {{ value || "-" }}
                </span>
              </div>
            </template>
          </div>
        </div>
        <div v-if="!formState.preview" class="col-6">
          <div class="row">
            <div v-if="formData.dn_for === 'invoice'" class="col-4">
              <span class="text-neutral-80">PO File(s)</span>
            </div>
            <div v-if="formData.dn_for === 'invoice'" class="col-8">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['Purchase Order']?.length"
                :files="files?.['Purchase Order']"
              />
              <span v-else>-</span>
            </div>
            <div v-if="formData.dn_for === 'bill'" class="col-4">
              <span class="text-neutral-80">PB File</span>
            </div>
            <div v-if="formData.dn_for === 'bill'" class="col-8">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['Purchase Bill']?.length"
                :files="files?.['Purchase Bill']"
              />
              <span v-else>-</span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">
                Ref.<span v-if="isQCGeneratedDebitNote"> QC</span>
                <span v-else> POD </span> File(s)
              </span>
            </div>
            <div class="col-8">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.[fileType]?.length"
                :files="files?.[fileType]"
              />
              <span v-else>-</span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">DN File(s)</span>
            </div>
            <div class="col-8">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['Debit Note']?.length"
                :files="files?.['Debit Note']"
              />
              <span v-else>-</span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">Ref. Invoice No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{
                  formData?.reference_invcs
                    ?.map((invc) => invc?.number)
                    .join(", ") || "-"
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <q-separator class="q-mb-xl q-mt-sm" />

    <div v-if="!formState.preview" class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6 column">
          <PInput
            :model-value="billing_address"
            class="col"
            label="Bill To"
            placeholder="Enter customer's billing address"
            type="textarea"
            autogrow
            disable
            required
          />
        </div>
        <div class="col-6 column">
          <PInput
            :model-value="shipping_address"
            class="col"
            label="Ship To"
            placeholder="Enter customer's shipping address"
            type="textarea"
            autogrow
            disable
            required
          />
        </div>
      </div>
    </div>

    <q-separator
      v-if="!(formState.view || formState.preview)"
      class="q-mb-xl q-mt-sm"
    />

    <div v-if="!(formState.view || formState.preview)" class="overflow-hidden">
      <div class="row">
        <div class="col-12">
          <PFile
            v-model="file"
            label="Attach Proof of Document (Debit Note)"
            help="Allowed file types are PDFs, images, excel, csv"
            required
          />
          <PFile
            v-if="file"
            v-model="file"
            class="q-mt-xxs"
            variant="re-upload"
          />
        </div>
      </div>
    </div>

    <q-separator class="q-mb-xl q-mt-sm" />

    <div class="overflow-hidden">
      <legend class="q-mb-sm text-neutral text-weight-medium text-md">
        Items
      </legend>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :columns="productListColumns"
        :pagination="{
          rowsPerPage: 25,
        }"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        class="text-xs"
        table-header-class="text-neutral-70"
        dense
        flat
        wrap-cells
      >
        <template #body-cell="props">
          <q-td :key="props.col.name" :props="props">
            <PEditable
              v-model="props.row[props.col.name]"
              :type="props.col.type"
              disable
            />
          </q-td>
        </template>

        <template #body-cell-name="props">
          <q-td :key="props.col.name" :props="props">
            <ItemNameCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-qty="props">
          <q-td :key="props.col.name" :props="props">
            <span class="ellipsis text-neutral">
              {{ props.value }} <br />
              {{ getPbUnit(props.row) || "Unit" }}
            </span>
          </q-td>
        </template>

        <template #body-cell-pb_qty="props">
          <q-td :key="props.col.name" :props="props">
            <span class="ellipsis text-neutral">
              {{ props.value }} <br />
              {{ getPbUnit(props.row) || "Unit" }}
            </span>
          </q-td>
        </template>

        <template #body-cell-po_qty="props">
          <q-td :key="props.col.name" :props="props">
            <span class="ellipsis text-neutral">
              {{ props.value }} <br />
              {{ props.row.po_unit || "Unit" }}
            </span>
          </q-td>
        </template>

        <template #body-cell-gst_exempted="props">
          <q-td :key="props.col.name" :props="props">
            <CheckMarkIcon
              v-if="props.row.gst_exempted"
              class="icon--sm text-primary"
            />
            <span v-else>-</span>
          </q-td>
        </template>

        <template #body-cell-rate="props">
          <q-td :key="props.col.name" :props="props">
            <ItemRateCell
              v-if="formState.preview"
              :col="props.col"
              :table-props="props"
            />
            <span v-else class="ellipsis text-neutral text-xs">
              <span class="block">
                {{
                  numberFormat({
                    number: getPbRate(props.row),
                    style: "currency",
                  })
                }}
                /
                {{ getPbUnit(props.row) || "Unit" }}
              </span>
              <span v-if="props.row.conversion_factor > 1" class="block">
                {{
                  numberFormat({
                    number: getPoRate(props.row),
                    style: "currency",
                  })
                }}
                /
                {{ props.row.po_unit || "Unit" }}
              </span>
              <span class="block text-neutral-70 text-xxs q-mb-xs">
                (exc. tax)
              </span>
              <span class="block">
                {{
                  numberFormat({
                    number: getRateWithTax(getPbRate(props.row), props.row),
                    style: "currency",
                  })
                }}
                /
                {{ getPbUnit(props.row) || "Unit" }}
              </span>
              <span v-if="props.row.conversion_factor > 1" class="block">
                {{
                  numberFormat({
                    number: getRateWithTax(getPoRate(props.row), props.row),
                    style: "currency",
                  })
                }}
                /
                {{ props.row.po_unit || "Unit" }}
              </span>
              <span class="block text-neutral-70 text-xxs">(inc. tax)</span>
            </span>
          </q-td>
        </template>

        <template #body-cell-amount="props">
          <q-td :key="props.col.name" :props="props">
            <ItemAmountCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-conversion_factor="props">
          <q-td :key="props.col.name" :props="props">
            <span
              v-if="getPbUnit(props.row) === props.row.po_unit"
              class="ellipsis text-neutral text-xs"
            >
              Same Units
            </span>
            <span v-else class="ellipsis text-neutral text-xs">
              {{ props.row.is_po_unit_bigger ? props.value : 1 }}
              {{ getPbUnit(props.row) || "Unit" }}
              =
              {{ props.row.is_po_unit_bigger ? 1 : props.value }}
              {{ props.row.po_unit || "Unit" }}
            </span>
          </q-td>
        </template>
      </PTable>
      <span v-else class="text-neutral-50">No Items</span>
    </div>

    <q-separator class="q-mb-xl q-mt-sm" />

    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div :class="[formState.preview ? 'col-12' : 'col-6']">
          <PInput
            v-model.trim="formData.remarks"
            label="Remarks (if any)"
            placeholder="Enter remarks"
            type="textarea"
            autogrow
            :disable="Boolean(formState.view || formState.preview)"
          />
        </div>
        <div v-if="!formState.preview" class="col-6">
          <div class="row">
            <div class="col-6">
              <p class="q-mb-none">Total Amount</p>
              <span class="text-xs text-neutral-70">Excludes Tax</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: getTotalAmountWithoutTax(),
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div class="row q-mt-lg">
            <div class="col-6">
              <p class="q-mb-none">Tax Total</p>
              <span class="text-xs text-neutral-70">GST + CESS</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div class="row q-mt-lg">
            <div class="col-6">
              <p class="q-mb-none">Grand Total</p>
              <span class="text-xs text-neutral-70"> Includes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div
            v-if="formData.dn_for === 'bill' && formData.tds"
            class="row q-mt-lg"
          >
            <div class="col-6">
              <TdsInfoTooltip :percentage="formData.tds?.percentage" />
            </div>
            <div class="col-6">
              <div class="text-right">
                -
                {{
                  numberFormat({
                    number: formData.tds?.amount,
                    style: "currency",
                  })
                }}
              </div>
              <PTooltip>{{ formData.seller?.tax_settings?.name }}</PTooltip>
            </div>
          </div>
          <div
            v-if="formData.dn_for === 'bill' && formData.tds"
            class="row q-mt-lg"
          >
            <div class="col-6">
              <p class="q-mb-none">Net Receivable</p>
              <span class="text-xs text-neutral-70">Vendor Credits</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount - (formData.tds?.amount ?? 0),
                    style: "currency",
                  })
                }}
              </div>
              <PTooltip>{{ formData.seller?.tax_settings?.name }}</PTooltip>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="formState.preview" #footer-info>
      <InfoPopover :info="netVendorCreditsInfo">
        <p class="q-mb-none">
          <span class="block text-neutral-80 text-xxs">Net Vendor Credits</span>
          <span class="text-sm">
            {{
              numberFormat({ number: formData.total_amount, style: "currency" })
            }}
          </span>
        </p>
      </InfoPopover>
    </template>
  </PForm>
</template>

<script setup>
import CheckMarkIcon from "assets/icons/checkmark.svg";
import AutoGenIconUrl from "assets/icons/auto-gen.svg?url";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import { date, useQuasar } from "quasar";
import * as fileApi from "src/api/file";
import PBadge from "src/components/PBadge.vue";
import PTooltip from "src/components/PTooltip.vue";
import debitNoteFormModel from "src/modules/accounts/models/debitNoteForm";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { useProductList } from "src/modules/purchase-order/composables/product-list";
import { numberFormat, roundOff } from "assets/scripts/utilities/helpers";
import { notify } from "assets/scripts/utilities/api-clients";

const $q = useQuasar();

const { formatDate } = date;

const pForm = ref(null);

const { formState, debitNote, hasGeneratedDebitNote } =
  storeToRefs(useDebitNoteStore());

const currentDate = computed(() => {
  if (unref(formState).preview || unref(formState).view) {
    return null;
  }

  return new Date();
});

const formHeading = computed(() => {
  let heading = "Debit Note";

  if (unref(formState).preview) {
    return `DN for PB: ${unref(formData).bill_number}`;
  }

  if (unref(formState).view) {
    return `View ${heading}`;
  }

  return `Add ${heading}`;
});

const {
  autoGenerateDebitNote,
  getDebitNotes,
  postDebitNote,
  putDebitNote,
  isDuplicate,
  toggleDebitNoteForm,
} = useDebitNoteStore();

watchEffect(() => {
  if (unref(formState).open && unref(debitNote).stage === "Not Started") {
    unref(debitNote).date = new Date();
  }
});

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: debitNoteFormModel(),
  entity: debitNote,
  formRef: pForm,
  isForm: formState,
  post: postDebitNote,
  put: putDebitNote,
  toggleForm: toggleDebitNoteForm,
});

const netVendorCreditsInfo = computed(() => {
  return [
    {
      label: "Total Amount (exc. tax)",
      value: unref(formData).total_amount - unref(formData).total_tax,
    },
    {
      label: "Total Tax",
      value: unref(formData).total_tax,
    },
    {
      label: "Grand Total",
      value: unref(formData).total_amount,
    },
    {
      label: `TDS (${unref(formData).tds?.percentage}%)`,
      value: unref(formData).tds?.amount,
    },
  ];
});

const { getTotalAmountWithoutTax } = useProductList({
  formData,
});

const debitNoteNumber = computed({
  get() {
    if (unref(formState.preview)) {
      return unref(formData).number || "Not Generated";
    }

    return unref(formData).number;
  },

  set(number) {
    if (!unref(formState).preview) {
      unref(formData).number = number;
    }
  },
});

const debitNoteInfo = computed(() => {
  const {
    bill_date,
    bill_number,
    dn_for,
    invc_date,
    invc_id,
    invc_number,
    po_id,
    po_info,
    seller,
  } = unref(formData);

  return [
    {
      label: "DN Against",
      value: dn_for === "bill" ? "Purchase Bill" : dn_for,
    },
    {
      label: dn_for === "bill" ? "PB No." : "Invoice No.",
      value: dn_for === "bill" ? bill_number : invc_number,
    },
    {
      label: dn_for === "bill" ? "PB Date" : "Invoice Date",
      value:
        dn_for === "bill"
          ? formatDate(bill_date, "D MMM YYYY")
          : formatDate(invc_date, "D MMM YYYY"),
    },
    {
      label: dn_for === "bill" ? "Vendor" : "Customer",
      value:
        dn_for === "bill"
          ? seller?.shop_name
          : po_info?.buyer_name || invc_id?.po_info?.buyer_name,
    },
    {
      label: "GSTIN",
      value:
        dn_for === "bill"
          ? seller?.gstin
          : po_info?.buyer_gstin || invc_id?.po_info?.buyer_gstin,
    },
    {
      label: dn_for === "invoice" && "PO No.",
      value: po_id?.number || invc_id?.po_info?.number,
    },
    {
      label: dn_for === "invoice" && "POSHN PO No.",
      value: po_id?.internal_number || invc_id?.po_info?.internal_number,
    },
  ];
});

const files = ref({});
const loadingFiles = ref(false);

const validatingNumber = ref(false);
const isDuplicateDNNumber = ref(false);

const shipping_address = computed(() => {
  if (unref(formData)?.shipping_address) {
    const shop_name = unref(formData)?.shipping_address_info?.shop_name
      ? `${unref(formData)?.shipping_address_info?.shop_name}\n`
      : "";

    return `${shop_name}${unref(formData)?.shipping_address}`;
  }

  return "";
});

const billing_address = computed(() => {
  if (unref(formData)?.billing_address) {
    const shop_name = unref(formData)?.billing_address_info?.shop_name
      ? `${unref(formData)?.billing_address_info?.shop_name}\n`
      : "";

    return `${shop_name}${unref(formData)?.billing_address}`;
  }

  return "";
});

const mapPods = (pods) => {
  if (pods?.length) {
    pods = pods.map((pod) => pod?._id || pod);
  } else {
    const { reference_invcs } = unref(formData);

    pods = reference_invcs?.reduce((pods, inv) => {
      if (inv?.pod_id) {
        pods.push(inv?.pod_id);
      }

      return pods;
    }, []);
  }

  return pods;
};

const getFiles = async () => {
  try {
    loadingFiles.value = true;

    let { _id, bill_id, invc_id, po_id, pods, qc_id } = unref(formData);
    const status_cd = "active";
    const fileGetters = [];

    files.value = {};

    if (_id) {
      fileGetters.push(
        fileApi.index({
          params: {
            dn_id: _id,
            status_cd,
          },
        }),
      );
    }

    if (qc_id) {
      fileGetters.push(
        fileApi.index({
          params: {
            qc_id,
            status_cd,
            fileType: "Quality Check",
          },
        }),
      );
    }

    if (bill_id) {
      fileGetters.push(
        fileApi.index({
          params: {
            bill_id: bill_id._id || bill_id,
            status_cd,
          },
        }),
      );
    }

    pods = mapPods(pods);

    if (pods?.length) {
      fileGetters.push(
        fileApi.index({
          params: {
            pod_id: pods.join(","),
            status_cd,
          },
        }),
      );
    }

    let poId;

    if (invc_id) {
      poId = invc_id?.po_id || po_id?._id || po_id;
    }

    if (poId) {
      fileGetters.push(
        fileApi.index({
          params: {
            po_id: poId,
            status_cd,
            fileType: "Purchase Order",
          },
        }),
      );
    }

    const response = await Promise.all(fileGetters);

    for (const entity of response) {
      let entityFiles = entity?.data?.data?.data;

      if (entityFiles?.length) {
        unref(files)[entityFiles[0]?.file_type] = entityFiles;
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    loadingFiles.value = false;
  }
};

const { isRightDrawerOpen } = storeToRefs(useAppStore());

const validateNumber = async (value) => {
  try {
    validatingNumber.value = true;

    isDuplicateDNNumber.value = false;

    const payload = { number: value, _id: unref(formData)._id };

    await isDuplicate(payload);

    validatingNumber.value = false;

    return true;
  } catch (error) {
    validatingNumber.value = false;

    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      isDuplicateDNNumber.value = true;

      return "Debit Note number already exists";
    }

    return true;
  }
};

watchEffect(() => {
  if (unref(isRightDrawerOpen)) {
    getFiles();
  }
});

const getConvertedQty = (lineItem) => {
  const { dn_for } = unref(formData);
  const { is_po_unit_bigger, conversion_factor, qty } = lineItem;

  return is_po_unit_bigger && dn_for === "bill"
    ? Number(qty) / Number(conversion_factor)
    : Number(qty) * Number(conversion_factor);
};

const getConvertedRate = (lineItem) => {
  const { dn_for } = unref(formData);
  const { conversion_factor, is_po_unit_bigger } = lineItem;

  return is_po_unit_bigger && dn_for === "bill"
    ? Number(getRate(lineItem)) * Number(conversion_factor)
    : Number(getRate(lineItem)) / Number(conversion_factor);
};

const getRate = (lineItem) => {
  const { dn_for, type_cd } = unref(formData);
  const { rate, pb_rate } = lineItem;

  let pbRate = rate;

  if (dn_for === "bill" && type_cd === "returns") {
    pbRate = pb_rate;
  }

  return roundOff(pbRate);
};

const getPbUnit = (lineItem) => {
  const { dn_for } = unref(formData);
  const { unit, pb_unit } = lineItem;

  return dn_for === "bill" ? unit : pb_unit;
};

const getPbRate = (lineItem) => {
  const { dn_for } = unref(formData);

  return dn_for === "bill" ? getRate(lineItem) : getConvertedRate(lineItem);
};

const getPoRate = (lineItem) => {
  const { dn_for } = unref(formData);

  return dn_for === "invoice" ? getRate(lineItem) : getConvertedRate(lineItem);
};

const getRateWithTax = (rate, lineItem) => {
  const { gst, cess } = lineItem;

  return roundOff(
    Number(rate) + Number(rate) * ((Number(gst) + Number(cess)) / 100),
  );
};

const productListColumns = computed(() => {
  let columns = [
    {
      align: "left",
      name: "name",
      label: "Name",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: "Qty",
      field: "qty",
      format: (qty, row) =>
        unref(formData).dn_for === "bill"
          ? roundOff(qty)
          : roundOff(getConvertedQty(row)) || "-",
      hidden: !unref(formState).preview,
    },
    {
      align: "left",
      name: "pb_qty",
      label: "PB Qty",
      field: "qty",
      format: (qty, row) =>
        unref(formData).dn_for === "bill"
          ? roundOff(qty)
          : roundOff(getConvertedQty(row)) || "-",
      hidden: unref(formState).preview,
    },
    {
      align: "left",
      name: "po_qty",
      label: "PO Qty",
      field: "qty",
      format: (qty, row) =>
        unref(formData).dn_for === "invoice"
          ? roundOff(qty)
          : roundOff(getConvertedQty(row)) || "-",
      hidden: unref(formState).preview,
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
    {
      align: "left",
      name: "conversion_factor",
      label: "Conv. Factor",
      field: "conversion_factor",
      hidden: unref(formState).preview,
    },
  ];

  return columns;
});

const submitButton = computed(() => {
  if (unref(formState).view) {
    return {};
  }

  if (unref(formState).preview) {
    if (unref(formData).number) {
      return {};
    }

    return {
      ...unref(oneClickGenerateButton),
      disable:
        unref(hasGeneratedDebitNote) || unref(formData)?.invc_id?.automation,
    };
  }

  return {
    loading: unref(isLoading),
    disable: unref(validatingNumber) || unref(isDuplicateDNNumber),
  };
});

const { getDebitNotesByPB } = useDebitNoteStore();
const { purchaseBill } = storeToRefs(usePurchaseBillStore());

let isAutoGenerating = ref(false);

const onConfirmAutoGeneration = async () => {
  try {
    isAutoGenerating.value = true;

    await autoGenerateDebitNote(unref(formData));

    if (unref(formState).preview) {
      await getDebitNotesByPB(unref(purchaseBill)._id);
    } else {
      await getDebitNotes();
    }

    closeForm(true);
  } catch (error) {
    console.error(error);
  } finally {
    isAutoGenerating.value = false;
  }
};

const confirmationMessage = computed(() => {
  return `<p>
            Your request will be generated with debit note number:
            <span class="text-weight-semibold">
              ${
                unref(formData).number ||
                "DN/" + unref(formData)?.bill_number ||
                ""
              }
            </span>
          </p>

          <span class="text-neutral-70 text-sm ${
            unref(formState).preview ? "hide" : ""
          }">
            (If you want to update the debit note number, then cancel this and <br/> fill in the number in the Debit Note No. field and click on 1-Click generate again.)
          </span>

          <br/>
          <br/>

          <span class="text-weight-medium">Are you sure want to proceed?</span>
      `;
});

const confirmAutoGeneration = () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      htmlContent: unref(confirmationMessage),
      labelClass: "text-center q-ma-xl rounded-borders",
      onConfirm: onConfirmAutoGeneration,
    },
  });
};

const oneClickGenerateButton = computed(() => {
  return {
    disable: unref(formData)?.invc_id?.automation,
    icon: `img:${AutoGenIconUrl}`,
    label: "1-Click generate",
    loading: unref(isAutoGenerating),
    onClick: confirmAutoGeneration,
    outline: true,
    type: "button",
  };
});

const secondaryCta = computed(() => {
  if (unref(formState).view || unref(formState).preview) {
    return {};
  }

  if (unref(formData).stage === "Not Started") {
    return unref(oneClickGenerateButton);
  }

  return { visible: true };
});

const { getCounts } = useAccountStore();
const { file, uploadFile } = useFile(formData);

const onSubmit = async () => {
  const submission = await submitForm(false);

  isLoading.value = true;

  await uploadFile({ type: "Debit Note" });

  await Promise.all([getCounts(), getDebitNotes()]);

  isLoading.value = false;

  closeForm(true);

  notify({
    type: "positive",
    message: submission?.data?.message,
  });
};
const isQCGeneratedDebitNote = computed(() => {
  return Boolean(unref(formData).qc_id);
});

const fileType = computed(() => {
  if (unref(isQCGeneratedDebitNote)) {
    return "Quality Check";
  }

  return "Proof of Delivery";
});
</script>
